// This is a part of the Cadcorp SIS source code. 
// Copyright (c) NEC Software Solutions UK Limited 1991-2026 
import { Overlay } from './Overlay';
import { IAjax, IExpressionBuilder, IQueryResponse } from '../../../../CommonScripts/Abstractions';
declare const ol: any;
import { CursorItem } from './Item';
import { QueryHelper } from './QueryHelper';

/**
 * Extends QueryHelper
 * @class 
 * @public
 * @export
 * @extends  Cadcorp.Web.Entities.GeognoSISJoinedOverlayQueryHelper#
 */
export class JoinedOverlayQueryHelper extends QueryHelper
{
    /**
    * @public
    * @property {Cadcorp.Web.Entities.GeognoSIS.Overlay} overlay - Overlay to perform queries on.
    * @memberof Cadcorp.Web.Entities.GeognoSIS.QueryHelper#
    */
    overlay: Overlay;
    /**
    * @property {string} m_cursorId - cursor id.
    * @memberof Cadcorp.Web.Entities.GeognoSISJoinedOverlayQueryHelper#
    * @private
    */
    private m_cursorId: string;
    /**
    * @property {String} m_cursorLink - link to the cursor.
    * @memberof Cadcorp.Web.Entities.GeognoSISJoinedOverlayQueryHelper#
    * @private
    */
    private m_cursorLink : string;
    /**
     * @constructor
     * @param {Overlay} overlay The overlay to query.
     * @param {IAjax} ajaxWrapper An IAjax implementation to perform asynchronous requests
     * @param {string} queryEndpoint The url to use to perform the query
     * @param {number} epsgCode The epsgcode of the layer to perform the query on
     * @public
     */
    constructor(overlay: Overlay, ajaxWrapper: IAjax, queryEndpoint: string, nextFeaturesEndpoint: string, epsgCode: number) {
        super({ sessionId: overlay.sessionId, index: overlay.overlayIndex, name: overlay.name },
            ajaxWrapper,
            queryEndpoint,
            nextFeaturesEndpoint,
            epsgCode,
            overlay.mapName);

        this.m_cursorLink = null;
        this.overlay = overlay;
    }
    /**
    * @inheritdoc 
    */
    findItems(map: any, expressionBuilder: IExpressionBuilder, maxFeaturesToGet: number, startPosition: number, queryBounds?: number[], ignoreBbox?: boolean, abort?: AbortController): Promise<IQueryResponse> {

        var dataObj = this.getQueryData(map, expressionBuilder, maxFeaturesToGet, startPosition, queryBounds, ignoreBbox);
        let options = null;
        if (abort)
            options = { signal: abort.signal };
        return this.ajaxWrapper.post(this.queryEndpoint, dataObj, options)
            .then((response: any) => {
                this.m_cursorId = response.cursorId;
                return response.cursorId;
            })
            .then((cursorId) => {
                var url = `${this.overlay.baseUrl}/${this.overlay.mapName}/${this.overlay.layerId}/Search/GeognoSis/${this.overlay.sessionId}/${this.overlay.overlayIndex}/Cursor/${cursorId}/Items`;
                return this.ajaxWrapper.post(url, {});
            }).
            then(response => {
                return this.queryCompleted(response);
            });
    }
    protected getQueryData(map: any, expressionBuilder: IExpressionBuilder, maxFeaturesToGet: number, startPosition: number, queryBounds?: number[], ignoreBbox?: boolean): any {

        const queryData = { expression: expressionBuilder.createExpression(map) }
        if (!ignoreBbox) {
            const bbox = this.getbbox(map, queryBounds);
            queryData['CRS'] = this.epsgCode.toString(),
            queryData['Bbox'] = bbox;
        }
        return queryData;
    }
    /**
    * Query Completed event handler, triggers item population.
    * @private
    * @function
    * @memberof Cadcorp.Web.Entities.GeognoSISJoinedOverlayQueryHelper#
    * @param {object} e - response object.
    * @returns {} 
    */
    protected queryCompleted(e: any): IQueryResponse {
        let returnObj = null;
        if (e.features && e.features.length > 0) {
            var foundItems = [];

            for (var a = 0; a < e.features.length; a++) {
                var feature = e.features[a];
                var item = new CursorItem({
                    feature: feature,
                    layerIndex: this.overlay.overlayIndex,
                    sessionId: this.overlay.sessionId,
                    mapName: this.overlay.mapName,
                    cursorId: this.m_cursorId
                });
                foundItems.push(item);
            }
            if (e.next) {
                returnObj = { foundItems: foundItems, next: decodeURIComponent(e.next) };
            }
            else
                returnObj = { foundItems: foundItems, next: null };

        }
        else {
            returnObj = { foundItems: [], next: null };
        }
        return returnObj;
    }

    /**
        * @export
        * Destroys this object.
        * @memberof Cadcorp.Web.Entities.GeognoSIS.CursorItem#
        */
    destroy(): Promise<JoinedOverlayQueryHelper> {
        //GeognoSis/{mapName}/{layerId:Guid}/Search/GeognoSis/{sessionId}/{overlayNo:int}/Cursor/{cursorId}/Items
        var url = `${this.overlay.baseUrl}/${this.mapName}/${this.overlay.layerId}/Search/GeognoSis/${this.overlay.sessionId}/${this.overlay.overlayIndex}/Cursor/${this.m_cursorId}`;

        return this.ajaxWrapper.delete(url).
            then(() => {
                super.destroy();
                return this;
            });
    }

};



